import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import clsx from 'clsx'
import type { ReactNode } from 'react'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  size?: ModalSize
}

const sizeMap: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export default function Modal({ open, onClose, children, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        onClose()
      }
    }
    if (open) window.addEventListener('keydown', handleEsc, true)
    return () => window.removeEventListener('keydown', handleEsc, true)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16,24,40,0.5)' }} className="animate-fade-in" onClick={onClose} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', pointerEvents: 'none', overflow: 'auto' }}>
        <div className={clsx('w-full bg-white rounded-2xl shadow-xl animate-scale-in my-auto', sizeMap[size])} style={{ pointerEvents: 'auto', maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>
    </>,
    document.body
  )
}

function ModalHeader({ children, onClose, className }: { children: ReactNode; onClose?: () => void; className?: string }) {
  return (
    <div className={clsx('flex items-center justify-between p-6 pb-0', className)}>
      <h2 className="text-xl font-semibold text-gray-900">{children}</h2>
      {onClose && (
        <button onClick={onClose} className="h-9 w-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      )}
    </div>
  )
}

function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('p-4 sm:p-6 overflow-y-auto flex-1', className)}>{children}</div>
}

function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex items-center gap-3 p-6 pt-0 justify-end', className)}>
      {children}
    </div>
  )
}

export { ModalHeader, ModalBody, ModalFooter }
